import {
  FeatherTrendingUp,
  FeatherTrendingDown,
  FeatherPieChart,
  FeatherDollarSign,
  FeatherFileText,
  FeatherCreditCard,
} from "@subframe/core";

export type MtdMetricIcon =
  | "trendingUp"
  | "trendingDown"
  | "pieChart"
  | "dollarSign"
  | "fileText"
  | "creditCard";

export interface MtdMetricCardProps {
  label: string;
  value: string;
  icon: MtdMetricIcon;
  onClick?: () => void;
  changeText?: string;
  changeVariant?: "success" | "error";
  subtext?: string;
  scopeFooterText?: string;
}

const IconComponent = {
  trendingUp: FeatherTrendingUp,
  trendingDown: FeatherTrendingDown,
  pieChart: FeatherPieChart,
  dollarSign: FeatherDollarSign,
  fileText: FeatherFileText,
  creditCard: FeatherCreditCard,
};

export function MtdMetricCard({
  label,
  value,
  changeText = "",
  changeVariant = "success",
  subtext = "",
  icon,
  onClick,
  scopeFooterText,
}: MtdMetricCardProps) {
  const Icon = IconComponent[icon];
  const changeClass =
    changeVariant === "success" ? "text-success-600" : "text-error-600";
  const isInteractive = typeof onClick === "function";
  const cardClassName = `flex grow shrink-0 basis-0 flex-col items-start overflow-hidden rounded-xl border border-solid border-neutral-200 ${
    isInteractive
      ? "cursor-pointer transition-colors hover:border-neutral-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      : ""
  }`;

  const footerComparison = (
    <div className="flex min-h-12 w-full min-w-0 items-center gap-1 border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3">
      <span
        className={`text-caption-bold font-caption-bold whitespace-nowrap ${changeClass}`}
      >
        {changeText}
      </span>
      <span className="text-caption font-caption text-neutral-500 truncate">
        {subtext}
      </span>
    </div>
  );

  const footerScope = (
    <div className="flex min-h-12 w-full min-w-0 items-center border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3">
      <span className="text-caption font-caption text-neutral-500">
        {scopeFooterText}
      </span>
    </div>
  );

  const footer =
    scopeFooterText !== undefined && scopeFooterText !== ""
      ? footerScope
      : footerComparison;

  const cardContent = (
    <>
      <div className="flex w-full flex-1 flex-col items-start gap-4 bg-white px-5 py-5 transition-colors">
        <div className="flex w-full items-start justify-between">
          <span className="text-body font-body text-neutral-700">{label}</span>
          <div className="flex h-8 w-8 flex-none flex-col items-center justify-center rounded-full bg-neutral-50 transition-colors">
            <Icon className="text-body font-body text-neutral-500 transition-colors" />
          </div>
        </div>
        <span className="text-heading-1 font-heading-1 text-neutral-900">
          {value}
        </span>
      </div>
      {footer}
    </>
  );

  if (isInteractive) {
    return (
      <button
        type="button"
        className={`${cardClassName} group transition-all hover:-translate-y-0.5 hover:bg-neutral-50 hover:shadow-lg active:translate-y-0 active:shadow-md focus-visible:shadow-lg`}
        onClick={onClick}
        aria-label={`Open ${label}`}
      >
        <div className="w-full">
          <div className="flex w-full flex-1 flex-col items-start gap-4 bg-white px-5 py-5 transition-colors group-hover:bg-neutral-50">
            <div className="flex w-full items-start justify-between">
              <span className="text-body font-body text-neutral-700">
                {label}
              </span>
              <div className="flex h-8 w-8 flex-none flex-col items-center justify-center rounded-full bg-neutral-50 transition-colors group-hover:bg-white">
                <Icon className="text-body font-body text-neutral-500 transition-colors group-hover:text-neutral-700" />
              </div>
            </div>
            <span className="text-heading-1 font-heading-1 text-neutral-900">
              {value}
            </span>
          </div>
          {scopeFooterText !== undefined && scopeFooterText !== "" ? (
            <div className="flex min-h-12 w-full min-w-0 items-center border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3 transition-colors group-hover:bg-white">
              <span className="text-caption font-caption text-neutral-500">
                {scopeFooterText}
              </span>
            </div>
          ) : (
            <div className="flex min-h-12 w-full min-w-0 items-center gap-1 border-t border-solid border-neutral-100 bg-neutral-50 px-5 py-3 transition-colors group-hover:bg-white">
              <span
                className={`text-caption-bold font-caption-bold whitespace-nowrap ${changeClass}`}
              >
                {changeText}
              </span>
              <span className="text-caption font-caption text-neutral-500 truncate">
                {subtext}
              </span>
            </div>
          )}
        </div>
      </button>
    );
  }

  return <div className={cardClassName}>{cardContent}</div>;
}
